import { DateTime } from "luxon"
import type { PendingCompanyBenefitEnrollmentDTO } from "@vitable/asclepius-api-client-ts"
import { yearFromIso } from "@common/utilities/time-utils"
import type {
  CompanyPendingElections,
  NonVpcBenefitElection,
  VpcBenefitElection
} from "@/modules/enrollment-new/enrollment-flow-types"

/** The pending elections one enrollment flow covers: a company's plan years starting in one year. */
export interface PendingElectionsGroup {
  companyId: string
  companyName: string
  /** `null` only when none of the group's plan years has a readable start date. */
  planYearStartYear: number | null
  vpcElection: VpcBenefitElection | undefined
  nonVpcElections: Array<NonVpcBenefitElection>
}

type GroupYear = number | null

export function planYearStartYear(enrollment: PendingCompanyBenefitEnrollmentDTO): GroupYear {
  return yearFromIso(enrollment.plan_year_coverage_start) ?? null
}

function byYear(a: GroupYear, b: GroupYear): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  return a - b
}

function earliestDeadline(enrollments: ReadonlyArray<PendingCompanyBenefitEnrollmentDTO>): string | null {
  let earliest: string | null = null
  for (const { enrollment_window_end: windowEnd } of enrollments) {
    if (!windowEnd) continue
    if (earliest === null || DateTime.fromISO(windowEnd) < DateTime.fromISO(earliest)) earliest = windowEnd
  }
  return earliest
}

/**
 * Splits a company's pending elections into one group per plan-year start year, earliest first. Only non-VPC
 * elections with an enrollment window end define a group; those without one join the earliest group, or form one by
 * their own start year when the company has nothing else. A VPC election joins the group of its own start year, or the
 * earliest group when it started before it (VPC spans years instead of renewing), creating a VPC-only group when
 * there is none.
 *
 * A flow holds a single VPC election. When two land in one group, the one starting that year wins, else the newest,
 * and the other moves to a group of its own start year. A second VPC election starting the same year as one stored
 * before it waits until that one is done, so a newly loaded one never displaces it.
 */
export function groupPendingElections(
  companyId: string,
  elections: CompanyPendingElections
): Array<PendingElectionsGroup> {
  const yearByNonVpcEnrollmentId = new Map<string, GroupYear>()
  const years = new Set<GroupYear>()
  const sortedYears = () => [...years].sort(byYear)

  const windowless: Array<NonVpcBenefitElection> = []
  for (const election of elections.nonVpcElections) {
    const year = planYearStartYear(election.enrollment)
    if (election.enrollment.enrollment_window_end && year !== null) {
      yearByNonVpcEnrollmentId.set(election.enrollment.id, year)
      years.add(year)
    } else {
      windowless.push(election)
    }
  }

  for (const election of windowless.sort((a, b) =>
    byYear(planYearStartYear(a.enrollment), planYearStartYear(b.enrollment))
  )) {
    const year = years.size > 0 ? sortedYears()[0] : planYearStartYear(election.enrollment)
    yearByNonVpcEnrollmentId.set(election.enrollment.id, year)
    years.add(year)
  }

  const vpcElectionByYear = placeVpcElections(elections.vpcElections, years.size > 0 ? sortedYears()[0] : undefined)
  for (const year of vpcElectionByYear.keys()) years.add(year)

  return sortedYears().map((year) => {
    const nonVpcElections = elections.nonVpcElections.filter(
      (election) => yearByNonVpcEnrollmentId.get(election.enrollment.id) === year
    )
    const vpcElection = vpcElectionByYear.get(year)
    return {
      companyId,
      companyName: (nonVpcElections[0] ?? vpcElection)?.enrollment.company_name ?? "",
      planYearStartYear: year,
      vpcElection,
      nonVpcElections
    }
  })
}

function placeVpcElections(
  vpcElections: ReadonlyArray<VpcBenefitElection>,
  earliestYear: GroupYear | undefined
): Map<GroupYear, VpcBenefitElection> {
  const byOwnYear = new Map<GroupYear, VpcBenefitElection>()
  for (const election of vpcElections) {
    const ownYear = planYearStartYear(election.enrollment)
    if (!byOwnYear.has(ownYear)) byOwnYear.set(ownYear, election)
  }

  const joinsEarliest = (ownYear: GroupYear) =>
    earliestYear !== undefined && earliestYear !== null && (ownYear === null || ownYear < earliestYear)

  const placed = new Map<GroupYear, VpcBenefitElection>()
  const movedToEarliest: Array<[GroupYear, VpcBenefitElection]> = []
  for (const [ownYear, election] of byOwnYear) {
    if (joinsEarliest(ownYear)) movedToEarliest.push([ownYear, election])
    else placed.set(ownYear, election)
  }
  if (earliestYear === undefined || earliestYear === null || movedToEarliest.length === 0) return placed

  // The earliest group keeps the VPC election starting that year, else the newest of those that started before it.
  const newestFirst = [...movedToEarliest].sort(([a], [b]) => byYearDescending(a, b))
  const [firstMoved, ...otherMoved] = newestFirst
  const losers = placed.has(earliestYear) ? newestFirst : otherMoved
  if (!placed.has(earliestYear) && firstMoved) placed.set(earliestYear, firstMoved[1])
  for (const [ownYear, election] of losers) placed.set(ownYear, election)
  return placed
}

function byYearDescending(a: GroupYear, b: GroupYear): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  return b - a
}

/**
 * The group's "enroll by" date: the soonest window end among its non-VPC elections still pending for the caller, so
 * an already answered election doesn't set it; a group with nothing else pending falls back to its VPC election.
 */
export function pendingGroupDeadline(
  group: PendingElectionsGroup,
  pendingEnrollmentIds: ReadonlySet<string>
): string | null {
  const pendingNonVpc = group.nonVpcElections
    .map((election) => election.enrollment)
    .filter((enrollment) => pendingEnrollmentIds.has(enrollment.public_id))
  if (pendingNonVpc.length > 0) return earliestDeadline(pendingNonVpc)
  return group.vpcElection ? earliestDeadline([group.vpcElection.enrollment]) : null
}

/** Whether the public enrollments endpoint still lists any of the group's elections as pending for the caller. */
export function isGroupPending(group: PendingElectionsGroup, pendingEnrollmentIds: ReadonlySet<string>): boolean {
  return [...group.nonVpcElections, ...(group.vpcElection ? [group.vpcElection] : [])].some((election) =>
    pendingEnrollmentIds.has(election.enrollment.public_id)
  )
}
